import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container, Typography, TextField, Button, Paper, Alert, Box } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';

const RESEND_COOLDOWN_SECONDS = 60;

const Verify2FA = () => {
  const { verifyTwoFactor, resendTwoFactor } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state || {};
  const [otpSessionId, setOtpSessionId] = useState(state.otp_session_id || '');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [maskedPhone, setMaskedPhone] = useState(state.masked_phone || '');
  const [maskedEmail, setMaskedEmail] = useState(state.masked_email || '');
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (!otpSessionId) {
      setError('Missing verification session. Please log in again.');
    }
  }, [otpSessionId]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setInterval(() => {
      setCooldown((prev) => Math.max(prev - 1, 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleVerify = async (event) => {
    event.preventDefault();
    setError('');
    if (!otpSessionId || code.trim().length !== 6) {
      setError('Enter the 6-digit code.');
      return;
    }

    setLoading(true);
    const result = await verifyTwoFactor({ otp_session_id: otpSessionId, code: code.trim() });
    setLoading(false);

    if (result?.success) {
      const role = result.user?.role;
      switch (role) {
        case 'admin':
          navigate('/dashboard'); break;
        case 'client':
          navigate('/client-dashboard'); break;
        case 'nurse':
          navigate('/nurse-dashboard'); break;
        case 'carer':
          navigate('/carer-dashboard'); break;
        default:
          navigate('/dashboard'); break;
      }
      return;
    }

    setError(result?.message || 'Invalid code. Please try again.');
  };

  const handleResend = async () => {
    setError('');
    if (!otpSessionId || cooldown > 0) return;
    const result = await resendTwoFactor({ otp_session_id: otpSessionId });
    if (!result?.success) {
      setError(result?.message || 'Unable to resend code.');
      return;
    }
    if (result.otp_session_id) {
      setOtpSessionId(result.otp_session_id);
    }
    setMaskedPhone(result.masked_phone || maskedPhone);
    setMaskedEmail(result.masked_email || maskedEmail);
    setCooldown(RESEND_COOLDOWN_SECONDS);
  };

  return (
    <Container maxWidth="xs">
      <Paper sx={{ p: 3, mt: 8 }}>
        <Typography variant="h5" gutterBottom>Verify Code</Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          We sent a 6-digit code to {maskedPhone || 'your phone'} and {maskedEmail || 'your email'}.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box component="form" onSubmit={handleVerify}>
          <TextField
            label="Verification code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            fullWidth
            margin="normal"
            required
            inputProps={{ inputMode: 'numeric' }}
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={loading || !otpSessionId}
            sx={{ mt: 2 }}
          >
            {loading ? 'Verifying...' : 'Verify'}
          </Button>
        </Box>
        <Box textAlign="right" mt={1}>
          <Button size="small" onClick={handleResend} disabled={!otpSessionId || cooldown > 0}>
            {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default Verify2FA;
